import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Row, Col, Alert, Spinner, Badge } from "reactstrap";
import Breadcrumb from "../../Components/Common/BreadCrumb";

// Import NEW unified detail components
import WindowsDetails from "./WindowsDetails";
import Office365Details from "./Office365Details";
import { hasFeature } from "../../helpers/features";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Index() {
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);

  const office365Enabled = hasFeature('office365');

  const isOffice365 = office365Enabled && (
    selectedThreat?.agent_id === "O365" ||
    selectedThreat?.agent_name?.includes("Office365")
  );
  const isAD        = selectedThreat?.agent_id === "AD";

  function getSeverity(level) {
    switch (true) {
      case level >= 12: return "Critical";
      case level >= 8:  return "High";
      case level >= 5:  return "Medium";
      case level >= 1:  return "Low";
      default:          return "Unknown";
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Critical": return "#8b0000";
      case "High":     return "#dc3545";
      case "Medium":   return "#ffc107";
      case "Low":      return "#198754";
      default:         return "#6c757d";
    }
  };

  const getAuthHeaders = () => {
    try {
      const authUser = JSON.parse(sessionStorage.getItem("authUser"));
      return {
        "Content-Type": "application/json",
        ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
      };
    } catch {
      return { "Content-Type": "application/json" };
    }
  };

  useEffect(() => {
    const fetchThreat = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const threatId  = urlParams.get("id");

        if (!threatId) {
          setError("No threat ID provided");
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/threats/${threatId}`, {
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          setError("Session expired. Please login again.");
          setTimeout(() => { window.location.href = "/login"; }, 2000);
          return;
        }

        const data = await response.json();

        if (data.success && data.threat) {
          setSelectedThreat(data.threat);
        } else {
          setError("Failed to load threat data");
        }
      } catch (err) {
        console.error("Error fetching threat data:", err);
        setError("Failed to load threat data");
      } finally {
        setLoading(false);
      }
    };

    fetchThreat();
  }, []);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="py-4 d-flex justify-content-center align-items-center min-vh-100">
          <div className="text-center">
            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading threat data...</p>
          </div>
        </Container>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger" className="mb-3">
            <i className="ri-error-warning-line me-2" />
            {error}
          </Alert>
        </Container>
      </div>
    );
  }

  // ── No data ────────────────────────────────────────────────────────────────
  if (!selectedThreat) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">No threat data available</Alert>
        </Container>
      </div>
    );
  }

  const severity      = getSeverity(selectedThreat.wazuh_rule_level);
  const severityColor = getSeverityColor(severity);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Threat Details" pageTitle="Threats" />

        {/* Source badge */}
        <Row className="mb-3">
          <Col>
            {isOffice365 ? (
              <Badge
                color="primary"
                className="px-3 py-2"
                style={{ backgroundColor: "#D83B01", fontSize: "0.9rem" }}
              >
                <i className="ri-microsoft-line me-2" />
                Office 365 Alert
              </Badge>
            ) : isAD ? (
              <Badge
                color="primary"
                className="px-3 py-2"
                style={{ backgroundColor: "#0078D4", fontSize: "0.9rem" }}
              >
                <i className="ri-shield-user-line me-2" />
                Active Directory
              </Badge>
            ) : (
              <Badge color="primary" className="px-3 py-2" style={{ fontSize: "0.9rem" }}>
                <i className="ri-windows-line me-2" />
                Windows Event
              </Badge>
            )}
          </Col>
        </Row>

        {/* Threat header card */}
        <Card className="mb-4" style={{ background: "linear-gradient(to right, #d87844ff, #f7e6e6eb)" }}>
          <CardBody>
            <Row className="justify-content-between align-items-center">
              <Col md="3" className="text-center">
                <button
                  className="rounded-pill text-white btn btn-outline-none px-4 mb-3"
                  style={{ backgroundColor: severityColor, border: "none", fontWeight: "500" }}
                >
                  {severity.toUpperCase()}
                </button>
                <h3 className="text-white mb-2">Security Rule {selectedThreat.wazuh_rule_id}</h3>
                <h6 className="text-white mb-0">
                  {isOffice365
                    ? (selectedThreat.office365_workload || "Office 365")
                    : selectedThreat.threat_type}
                </h6>
              </Col>

              <Col md="5" className="text-center">
                <h5 className="text-dark mb-2">{selectedThreat.wazuh_rule_description}</h5>
              </Col>

              <Col md="4" className="text-center">
                <h6 className="text-dark mb-2">Detected At</h6>
                <h6 className="text-white mb-0">
                  {new Date(selectedThreat.timestamp).toLocaleString()}
                </h6>
                {isOffice365 && selectedThreat.office365_geo_location && (
                  <Badge color="secondary" className="mt-2">
                    <i className="ri-global-line me-1" />
                    {selectedThreat.office365_geo_location}
                  </Badge>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Detail panel — Office 365 or Windows/default */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-3">
            {isOffice365 ? (
              <Office365Details threat={selectedThreat} />
            ) : (
              <WindowsDetails threat={selectedThreat} />
            )}
          </CardBody>
        </Card>

      </Container>
    </div>
  );
}

export default Index;